import { createContext, useContext, type ReactNode } from 'react';
import { useAgentChat } from '~/hooks/use-agent-chat';

// Hosts the single chat instance one level above the route Outlet so the
// active stream survives navigating between inbox and other sections —
// without this provider, `useAgentChat` would be created inside the inbox
// route and the AI SDK's per-instance fetch would be torn down on every
// sibling-route switch, cutting off in-flight replies.
type ChatContextValue = ReturnType<typeof useAgentChat>;

const ChatContext = createContext<ChatContextValue | null>(null);

// FRONT-2195: `reattach: false` — no surface renders this instance's
// messages (the chat window, day-one chat and operator panel each host their
// own `useAgentChat`), yet it binds to the canonical session, which is usually
// the very chat the person reopens. Re-attaching, it would open a second,
// redundant resume connection to a running turn nobody sees here, and every
// replay would re-claim and release the run's FRONT-1717 history write. (Before
// pursuits were keyed per instance it was worse: it mounted first on a reload,
// took the live stream, and left the open chat window frozen.)
export function ChatProvider({ children }: { children: ReactNode }) {
  const chat = useAgentChat(undefined, { reattach: false });
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>;
}

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChatContext must be used inside <ChatProvider>');
  }
  return ctx;
}
